import React, { useEffect, useState } from 'react';
import './Login.scss';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from './Logo';
import SendEmail from './components/SendEmail';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { loginUser } from '../../../crud/users_api/auth';
import { validateUserEmail } from '../../../crud/users_api/users';
import { useAuth } from '../../../components/AuthManagement/AuthContext';

const LoginPage: React.FC = () => {
  const { user_id } = useParams<{ user_id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUserData } = useAuth();
  const newCreatedUser = JSON.parse(localStorage.getItem('newCreatedUser') || '{}');

  const urlEmail = searchParams.get('email');
  const urlPassword = searchParams.get('pwd');

  const [username, setUsername] = useState(urlEmail || newCreatedUser.email || '');
  const [password, setPassword] = useState(urlPassword || newCreatedUser.password || '');
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const togglePasswordVisibility = () => setIsPasswordShown(!isPasswordShown);
  const handleForgotPasswordClick = () => setIsForgotPassword(true);
  const handleBackClick = () => setIsForgotPassword(false);

  useEffect(() => {
    if (user_id) {
      validateUserEmail(Number(user_id))
        .then((response) => {
          if (response.status === 200) {
            setVerified(true);
            setVerificationMessage(`Bienvenido ${response.data.username}`);
          } else {
            setVerified(false);
            setVerificationMessage('No se pudo verificar el usuario');
          }
        })
        .catch(() => {
          setVerified(false);
          setVerificationMessage('No se pudo verificar el usuario');
        });
    }
  }, [user_id]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await loginUser(username.trim(), password, setLoading, setError);

      if (response.status === 200) {
        await setUserData(response.data.user);
        localStorage.removeItem('newCreatedUser');
        localStorage.removeItem('loggedOut');
        navigate('/App');
      } else {
        setError(response.message || 'An error occurred');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="lp-loading">
        <div className="lp-loading__spinner" />
        <span className="lp-loading__text">Un segundo...</span>
      </div>
    );
  }

  return (
    <div className="lp">
      {/* Ambient background layers */}
      <div className="lp__bg" />
      <div className="lp__bg-glow lp__bg-glow--1" />
      <div className="lp__bg-glow lp__bg-glow--2" />
      <div className="lp__noise" />

      {/* Main card */}
      <div className="lp__card">
        {/* Verification banner */}
        {user_id && (
          <div className={`lp__banner ${verified ? 'lp__banner--success' : 'lp__banner--error'}`}>
            <span>{verified ? verificationMessage : 'No se pudo verificar el usuario.'}</span>
          </div>
        )}

        {isForgotPassword ? (
          <SendEmail onBack={handleBackClick} />
        ) : (
          <>
            <div className="lp__header">
              <Logo className="lp__logo-animate" />
              <p className="lp__tagline">Gestión inteligente para tu restaurante</p>
            </div>

            <form onSubmit={handleSubmit} className="lp__form">
              <div className="lp__field">
                <label htmlFor="username" className="lp__label">Correo electrónico</label>
                <input
                  type="text"
                  id="username"
                  className="lp__input"
                  value={username.trim()}
                  placeholder="tu@correo.com"
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="lp__field">
                <label htmlFor="password" className="lp__label">Contraseña</label>
                <div className="lp__input-wrap">
                  <input
                    type={isPasswordShown ? 'text' : 'password'}
                    id="password"
                    className="lp__input"
                    value={password}
                    placeholder="••••••••"
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="lp__eye"
                    aria-label={isPasswordShown ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon icon={isPasswordShown ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handleForgotPasswordClick}
                className="lp__forgot"
              >
                ¿Olvidaste tu contraseña?
              </button>

              {error && (
                <div className="lp__error" role="alert">{error}</div>
              )}

              <button type="submit" className="lp__submit">
                Ingresar
              </button>
            </form>

            <div className="lp__divider">
              <span>o</span>
            </div>

            <div className="lp__footer">
              <p>
                ¿Aún no eres cliente?{' '}
                <a href="https://example.com/contacto" className="lp__link">
                  Comunícate con nosotros
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
